import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import CopyBarcodeSerial from "../controls/CopyBarcodeSerial";
import ScannableBarcode from "../forms/traceability/receivingBarcode/ScannableBarcode";
import { isDeveloperModeEnabled } from "../../shared/developerMode";
import {
  formatCurrency,
  formatDate,
  formatQuantity,
} from "../../shared/utility";

function placeName(value) {
  if (value == null || String(value).trim() === "") {
    return "—";
  }
  return value;
}

function weightBody(row, unitOfMeasure) {
  return formatQuantity(
    row.stock_in_weight,
    row.unit_of_measure || unitOfMeasure,
  );
}

function dollarBody(row) {
  return (
    <span className="tabular-nums font-medium text-[#0F172A]">
      {formatCurrency(row.total_dollar_amount)}
    </span>
  );
}

function InventoryManufacturedSubTable(props) {
  const {
    groups,
    loading,
    zonesByKey,
    loadingZones,
    zoneKeyFor,
    onExpandLocation,
    lotsByKey,
    loadingLots,
    lotKeyFor,
    onExpandZone,
    cartonsByKey,
    loadingCartons,
    cartonKeyFor,
    onExpandLot,
    unitOfMeasure,
  } = props;

  const [expandedLocations, setExpandedLocations] = useState(null);

  if (loading) {
    return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
  }

  const cartonExpansion = (lot) => {
    const key = cartonKeyFor(lot);
    if (loadingCartons[key]) {
      return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
    }

    const cartons = cartonsByKey[key] ?? [];

    return (
      <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
        <DataTable
          value={cartons}
          className="p-datatable-sm text-sm"
          dataKey="id"
          emptyMessage="No cartons in this lot."
        >
          <Column
            header="Barcode serial"
            body={(row) => {
              const barcode = row.shipping_barcode || row.serial_number;
              return (
                <div>
                  <CopyBarcodeSerial barcode={barcode} />
                  {isDeveloperModeEnabled() && barcode ? (
                    <ScannableBarcode value={barcode} showValue={false} />
                  ) : null}
                </div>
              );
            }}
            style={{ minWidth: "12rem" }}
          />
          <Column
            header="Packaging date"
            body={(row) => formatDate(row.packaging_date)}
            style={{ minWidth: "9rem" }}
          />
          <Column
            header="Case weight"
            alignHeader="right"
            body={(row) =>
              formatQuantity(
                row.packing_size,
                row.unit_of_measure || unitOfMeasure,
              )
            }
            style={{ minWidth: "8rem", textAlign: "right" }}
            bodyClassName="text-right tabular-nums"
          />
        </DataTable>
      </div>
    );
  };

  const lotExpansion = (zone) => {
    const key = lotKeyFor(zone);
    if (loadingLots[key]) {
      return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
    }

    return (
      <LotTable
        lots={lotsByKey[key] ?? []}
        unitOfMeasure={unitOfMeasure}
        cartonExpansion={cartonExpansion}
        onExpandLot={onExpandLot}
      />
    );
  };

  const zoneExpansion = (location) => {
    const key = zoneKeyFor(location);
    if (loadingZones[key]) {
      return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
    }

    return (
      <ZoneTable
        zones={zonesByKey[key] ?? []}
        unitOfMeasure={unitOfMeasure}
        lotExpansion={lotExpansion}
        onExpandZone={onExpandZone}
      />
    );
  };

  return (
    <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
      <DataTable
        value={groups ?? []}
        expandedRows={expandedLocations}
        onRowToggle={(event) => setExpandedLocations(event.data)}
        onRowExpand={(event) => onExpandLocation(event.data)}
        rowExpansionTemplate={zoneExpansion}
        dataKey="group_key"
        className="p-datatable-sm text-sm"
        emptyMessage="No stock in the selected location or zone."
      >
        <Column
          header="Location"
          body={(row) => placeName(row.location_name)}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Quantity in stock by weight"
          alignHeader="right"
          body={(row) => weightBody(row, unitOfMeasure)}
          style={{ minWidth: "12rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Quantity in stock/case"
          alignHeader="right"
          field="total_individual_units"
          style={{ minWidth: "10rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Total Dollar Amount"
          alignHeader="right"
          body={dollarBody}
          style={{ minWidth: "9rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column expander={() => true} header="" style={{ width: "5rem" }} />
      </DataTable>
    </div>
  );
}

function ZoneTable({ zones, unitOfMeasure, lotExpansion, onExpandZone }) {
  const [expandedZones, setExpandedZones] = useState(null);

  return (
    <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
      <DataTable
        value={zones}
        expandedRows={expandedZones}
        onRowToggle={(event) => setExpandedZones(event.data)}
        onRowExpand={(event) => onExpandZone(event.data)}
        rowExpansionTemplate={lotExpansion}
        dataKey="group_key"
        className="p-datatable-sm text-sm"
        emptyMessage="No zones in this location."
      >
        <Column
          header="Zone"
          body={(row) => placeName(row.zone_name)}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Quantity in stock by weight"
          alignHeader="right"
          body={(row) => weightBody(row, unitOfMeasure)}
          style={{ minWidth: "12rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Quantity in stock/case"
          alignHeader="right"
          field="total_individual_units"
          style={{ minWidth: "10rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Total Dollar Amount"
          alignHeader="right"
          body={dollarBody}
          style={{ minWidth: "9rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column expander={() => true} header="" style={{ width: "5rem" }} />
      </DataTable>
    </div>
  );
}

function LotTable({ lots, unitOfMeasure, cartonExpansion, onExpandLot }) {
  const [expandedLots, setExpandedLots] = useState(null);

  return (
    <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
      <DataTable
        value={lots}
        expandedRows={expandedLots}
        onRowToggle={(event) => setExpandedLots(event.data)}
        onRowExpand={(event) => onExpandLot(event.data)}
        rowExpansionTemplate={cartonExpansion}
        dataKey="group_key"
        className="p-datatable-sm text-sm"
        emptyMessage="No lots in this zone."
      >
        <Column
          header="Lot"
          body={(row) => placeName(row.lot_number)}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Quantity in stock by weight"
          alignHeader="right"
          body={(row) => weightBody(row, unitOfMeasure)}
          style={{ minWidth: "12rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Quantity in stock/case"
          alignHeader="right"
          field="total_individual_units"
          style={{ minWidth: "10rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Total Dollar Amount"
          alignHeader="right"
          body={dollarBody}
          style={{ minWidth: "9rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column expander={() => true} header="" style={{ width: "5rem" }} />
      </DataTable>
    </div>
  );
}

export default InventoryManufacturedSubTable;
